{
  "version": "0.1.38",
  "packageName": "@classic-homes/auth",
  "description": "Authentication services and Svelte bindings for Classic Theme apps",
  "subpackages": {
    "core": {
      "importPath": "@classic-homes/auth/core",
      "description": "Framework-agnostic authentication core. Use this for direct API access or custom integrations.",
      "modules": {
        "config": {
          "description": "Authentication configuration and initialization",
          "functions": [
            {
              "name": "initAuth",
              "signature": "(config: AuthConfig) => void",
              "description": "Initialize the auth system with configuration options",
              "example": "import { initAuth } from '@classic-homes/auth/core';\n\ninitAuth({\n  baseUrl: 'https://api.example.com',\n  storage: localStorage,\n  sso: {\n    enabled: true,\n    authorizeUrl: 'https://sso.example.com/authorize'\n  }\n});"
            },
            {
              "name": "getConfig",
              "signature": "() => AuthConfig",
              "description": "Get the current auth configuration"
            },
            {
              "name": "isInitialized",
              "signature": "() => boolean",
              "description": "Check if auth has been initialized"
            },
            {
              "name": "resetConfig",
              "signature": "() => void",
              "description": "Reset auth configuration to defaults"
            },
            {
              "name": "getDefaultStorage",
              "signature": "() => StorageAdapter",
              "description": "Get the default storage adapter (localStorage with fallback)"
            },
            {
              "name": "getStorage",
              "signature": "() => StorageAdapter",
              "description": "Get the configured storage adapter"
            },
            {
              "name": "getFetch",
              "signature": "() => typeof fetch",
              "description": "Get the configured fetch function"
            }
          ],
          "types": [
            {
              "name": "AuthConfig",
              "description": "Configuration options for auth initialization",
              "properties": [
                {
                  "name": "baseUrl",
                  "type": "string",
                  "required": true,
                  "description": "Base URL for auth API endpoints"
                },
                {
                  "name": "storage",
                  "type": "StorageAdapter",
                  "required": false,
                  "description": "Storage adapter for tokens"
                },
                {
                  "name": "fetch",
                  "type": "typeof fetch",
                  "required": false,
                  "description": "Custom fetch implementation"
                },
                {
                  "name": "sso",
                  "type": "SSOConfig",
                  "required": false,
                  "description": "SSO configuration"
                }
              ]
            },
            {
              "name": "SSOConfig",
              "description": "SSO provider configuration",
              "properties": [
                {
                  "name": "enabled",
                  "type": "boolean",
                  "required": false,
                  "description": "Whether SSO is enabled"
                },
                {
                  "name": "authorizeUrl",
                  "type": "string",
                  "required": false,
                  "description": "SSO authorization URL"
                }
              ]
            },
            {
              "name": "StorageAdapter",
              "description": "Interface for token storage",
              "properties": [
                {
                  "name": "getItem",
                  "type": "(key: string) => string | null",
                  "required": true,
                  "description": "Get item from storage"
                },
                {
                  "name": "setItem",
                  "type": "(key: string, value: string) => void",
                  "required": true,
                  "description": "Set item in storage"
                },
                {
                  "name": "removeItem",
                  "type": "(key: string) => void",
                  "required": true,
                  "description": "Remove item from storage"
                }
              ]
            }
          ]
        },
        "client": {
          "description": "HTTP client utilities for API requests",
          "functions": [
            {
              "name": "api",
              "signature": "{ get, post, put, delete }",
              "description": "HTTP client object with methods for making API requests"
            },
            {
              "name": "apiRequest",
              "signature": "(url: string, options?: ApiRequestOptions) => Promise<Response>",
              "description": "Make an authenticated API request"
            },
            {
              "name": "extractData",
              "signature": "<T>(response: { data: T } | T) => T",
              "description": "Extract data from API response wrapper"
            },
            {
              "name": "getAccessToken",
              "signature": "() => string | null",
              "description": "Get the stored access token"
            },
            {
              "name": "getRefreshToken",
              "signature": "() => string | null",
              "description": "Get the stored refresh token"
            },
            {
              "name": "getSessionToken",
              "signature": "() => string | null",
              "description": "Get the stored session token"
            },
            {
              "name": "updateStoredTokens",
              "signature": "(tokens: { accessToken?: string; refreshToken?: string; sessionToken?: string }) => void",
              "description": "Update stored tokens"
            },
            {
              "name": "clearStoredAuth",
              "signature": "() => void",
              "description": "Clear all stored authentication data"
            }
          ],
          "types": [
            {
              "name": "ApiRequestOptions",
              "description": "Options for API requests"
            },
            {
              "name": "ApiResponse",
              "description": "Standard API response wrapper"
            }
          ]
        },
        "api": {
          "description": "Auth API client methods for all authentication endpoints",
          "functions": [
            {
              "name": "authApi.login",
              "signature": "(credentials: LoginCredentials) => Promise<LoginResponse>",
              "description": "Login with username and password",
              "example": "import { authApi } from '@classic-homes/auth/core';\n\nconst response = await authApi.login({\n  username: 'user@example.com',\n  password: 'password'\n});\n\nif (response.requiresMFA) {\n  // Handle MFA challenge\n} else {\n  // Login success\n  console.log(response.user);\n}"
            },
            {
              "name": "authApi.logout",
              "signature": "() => Promise<LogoutResponse>",
              "description": "Logout the current user. Returns SSO logout URL if applicable."
            },
            {
              "name": "authApi.register",
              "signature": "(data: RegisterData) => Promise<RegisterResponse>",
              "description": "Register a new user"
            },
            {
              "name": "authApi.forgotPassword",
              "signature": "(email: string) => Promise<void>",
              "description": "Request a password reset email"
            },
            {
              "name": "authApi.resetPassword",
              "signature": "(token: string, newPassword: string) => Promise<void>",
              "description": "Reset password with a token"
            },
            {
              "name": "authApi.refreshToken",
              "signature": "(refreshToken: string) => Promise<{ accessToken: string; refreshToken: string }>",
              "description": "Refresh the access token"
            },
            {
              "name": "authApi.initiateSSOLogin",
              "signature": "(options?: { callbackUrl?: string; redirectUrl?: string }) => void",
              "description": "Initiate SSO login by redirecting to the SSO provider"
            },
            {
              "name": "authApi.getProfile",
              "signature": "(customFetch?: typeof fetch) => Promise<User>",
              "description": "Get the current user's profile"
            },
            {
              "name": "authApi.updateProfile",
              "signature": "(data: ProfileUpdateData) => Promise<User>",
              "description": "Update the current user's profile"
            },
            {
              "name": "authApi.changePassword",
              "signature": "(data: ChangePasswordData) => Promise<void>",
              "description": "Change the current user's password"
            },
            {
              "name": "authApi.resendVerification",
              "signature": "() => Promise<void>",
              "description": "Resend email verification"
            },
            {
              "name": "authApi.verifyEmail",
              "signature": "(token: string) => Promise<{ message: string; user?: User }>",
              "description": "Verify email with a token"
            },
            {
              "name": "authApi.getSessions",
              "signature": "(customFetch?: typeof fetch) => Promise<{ sessions: Session[]; total: number }>",
              "description": "Get all active sessions"
            },
            {
              "name": "authApi.revokeSession",
              "signature": "(sessionId: string) => Promise<void>",
              "description": "Revoke a specific session"
            },
            {
              "name": "authApi.revokeAllSessions",
              "signature": "() => Promise<void>",
              "description": "Revoke all sessions except the current one"
            },
            {
              "name": "authApi.getApiKeys",
              "signature": "(customFetch?: typeof fetch) => Promise<{ apiKeys: ApiKey[] }>",
              "description": "Get all API keys"
            },
            {
              "name": "authApi.createApiKey",
              "signature": "(data: CreateApiKeyRequest) => Promise<CreateApiKeyResponse>",
              "description": "Create a new API key"
            },
            {
              "name": "authApi.revokeApiKey",
              "signature": "(keyId: string) => Promise<void>",
              "description": "Revoke an API key"
            },
            {
              "name": "authApi.updateApiKey",
              "signature": "(keyId: string, data: { name: string }) => Promise<void>",
              "description": "Update an API key's name"
            },
            {
              "name": "authApi.getMFAStatus",
              "signature": "() => Promise<MFAStatus>",
              "description": "Get MFA status for the current user"
            },
            {
              "name": "authApi.setupMFA",
              "signature": "() => Promise<MFASetupResponse>",
              "description": "Setup MFA (get QR code and backup codes)"
            },
            {
              "name": "authApi.verifyMFASetup",
              "signature": "(code: string) => Promise<void>",
              "description": "Verify MFA setup with a code"
            },
            {
              "name": "authApi.disableMFA",
              "signature": "(password: string) => Promise<void>",
              "description": "Disable MFA"
            },
            {
              "name": "authApi.verifyMFAChallenge",
              "signature": "(data: MFAChallengeData) => Promise<LoginResponse>",
              "description": "Verify MFA challenge during login"
            },
            {
              "name": "authApi.getDevices",
              "signature": "(customFetch?: typeof fetch) => Promise<{ devices: Device[] }>",
              "description": "Get all devices"
            },
            {
              "name": "authApi.trustDevice",
              "signature": "(deviceId: string) => Promise<void>",
              "description": "Trust a device"
            },
            {
              "name": "authApi.revokeDevice",
              "signature": "(deviceId: string) => Promise<void>",
              "description": "Revoke device trust"
            },
            {
              "name": "authApi.removeDevice",
              "signature": "(deviceId: string) => Promise<void>",
              "description": "Remove a device completely"
            },
            {
              "name": "authApi.approveDevice",
              "signature": "(token: string) => Promise<{ message: string; device?: Device }>",
              "description": "Approve a device with a token"
            },
            {
              "name": "authApi.blockDevice",
              "signature": "(token: string) => Promise<{ message: string; device?: Device }>",
              "description": "Block a device with a token"
            },
            {
              "name": "authApi.getPreferences",
              "signature": "(customFetch?: typeof fetch) => Promise<UserPreferences>",
              "description": "Get user preferences"
            },
            {
              "name": "authApi.updatePreferences",
              "signature": "(data: Partial<UserPreferences>) => Promise<void>",
              "description": "Update user preferences"
            },
            {
              "name": "authApi.getSSOAccounts",
              "signature": "(customFetch?: typeof fetch) => Promise<LinkedAccount[]>",
              "description": "Get SSO linked accounts"
            },
            {
              "name": "authApi.unlinkSSOAccount",
              "signature": "(provider: string, password?: string) => Promise<void>",
              "description": "Unlink an SSO account"
            },
            {
              "name": "authApi.linkSSOAccount",
              "signature": "(provider?: string) => Promise<void>",
              "description": "Link an SSO account (redirects to SSO provider)"
            },
            {
              "name": "authApi.getSecurityEvents",
              "signature": "(params?: { page?: number; limit?: number; type?: string }, customFetch?: typeof fetch) => Promise<{ events: SecurityEvent[]; pagination: Pagination }>",
              "description": "Get security event history"
            },
            {
              "name": "authApi.regenerateBackupCodes",
              "signature": "(password: string) => Promise<{ backupCodes: string[] }>",
              "description": "Regenerate MFA backup codes"
            }
          ]
        },
        "service": {
          "description": "Auth service with automatic store updates",
          "functions": [
            {
              "name": "AuthService",
              "signature": "class",
              "description": "Service class that wraps auth API and updates stores automatically",
              "example": "import { authService } from '@classic-homes/auth/core';\n\n// Use the singleton instance\nconst response = await authService.login({\n  username: 'user@example.com',\n  password: 'password'\n});\n\n// Or create your own instance\nconst service = new AuthService({ storeUpdate: myStoreUpdateFn });"
            },
            {
              "name": "authService",
              "signature": "AuthService",
              "description": "Singleton instance of AuthService"
            }
          ],
          "types": [
            {
              "name": "LoginOptions",
              "description": "Options for login operation",
              "properties": [
                {
                  "name": "updateStore",
                  "type": "boolean",
                  "required": false,
                  "description": "Whether to update auth store"
                }
              ]
            },
            {
              "name": "MFAVerifyOptions",
              "description": "Options for MFA verification",
              "properties": [
                {
                  "name": "updateStore",
                  "type": "boolean",
                  "required": false,
                  "description": "Whether to update auth store"
                }
              ]
            }
          ]
        },
        "guards": {
          "description": "Type guards for auth responses",
          "functions": [
            {
              "name": "isMfaChallengeResponse",
              "signature": "(response: LoginResponse) => boolean",
              "description": "Check if a login response requires MFA verification",
              "example": "import { authApi, isMfaChallengeResponse } from '@classic-homes/auth/core';\n\nconst response = await authApi.login(credentials);\n\nif (isMfaChallengeResponse(response)) {\n  // Redirect to MFA verification\n  const mfaToken = getMfaToken(response);\n} else {\n  // Login complete\n}"
            },
            {
              "name": "isLoginSuccessResponse",
              "signature": "(response: LoginResponse) => boolean",
              "description": "Check if a login response is a success (not MFA challenge)"
            },
            {
              "name": "getMfaToken",
              "signature": "(response: LoginResponse) => string | undefined",
              "description": "Extract MFA token from a login response"
            },
            {
              "name": "getAvailableMethods",
              "signature": "(response: LoginResponse) => string[]",
              "description": "Get available MFA methods from a login response"
            }
          ]
        },
        "jwt": {
          "description": "JWT token utilities",
          "functions": [
            {
              "name": "decodeJWT",
              "signature": "(token: string) => JWTPayload | null",
              "description": "Decode a JWT token without verification",
              "example": "import { decodeJWT, isTokenExpired } from '@classic-homes/auth/core';\n\nconst payload = decodeJWT(accessToken);\nif (payload && !isTokenExpired(accessToken)) {\n  console.log('User ID:', payload.sub);\n  console.log('Expires:', new Date(payload.exp * 1000));\n}"
            },
            {
              "name": "isTokenExpired",
              "signature": "(token: string) => boolean",
              "description": "Check if a JWT token is expired"
            },
            {
              "name": "getTokenRemainingTime",
              "signature": "(token: string) => number",
              "description": "Get remaining time in milliseconds until token expires"
            },
            {
              "name": "getTokenExpiration",
              "signature": "(token: string) => Date | null",
              "description": "Get the expiration date of a token"
            },
            {
              "name": "extractClaims",
              "signature": "<T extends object>(token: string) => T | null",
              "description": "Extract custom claims from a JWT token"
            }
          ],
          "types": [
            {
              "name": "JWTPayload",
              "description": "Standard JWT payload structure",
              "properties": [
                {
                  "name": "sub",
                  "type": "string",
                  "required": true,
                  "description": "Subject (user ID)"
                },
                {
                  "name": "exp",
                  "type": "number",
                  "required": true,
                  "description": "Expiration timestamp"
                },
                {
                  "name": "iat",
                  "type": "number",
                  "required": true,
                  "description": "Issued at timestamp"
                },
                {
                  "name": "iss",
                  "type": "string",
                  "required": false,
                  "description": "Issuer"
                },
                {
                  "name": "aud",
                  "type": "string | string[]",
                  "required": false,
                  "description": "Audience"
                }
              ]
            }
          ]
        },
        "errors": {
          "description": "Custom error types for authentication operations",
          "functions": [
            {
              "name": "RoleDeniedError",
              "signature": "class extends Error",
              "description": "Error thrown when a user authenticates but lacks required roles",
              "example": "import { authService, RoleDeniedError } from '@classic-homes/auth/core';\n\ntry {\n  const response = await authService.login(credentials, {\n    allowedRoles: ['admin', 'manager', 'agent'],\n  });\n} catch (error) {\n  if (error instanceof RoleDeniedError) {\n    // User authenticated but lacks required role\n    console.log('User roles:', error.user.roles);\n    console.log('Required roles:', error.requiredRoles);\n    goto('/auth/login?error=insufficient_permissions');\n  }\n}"
            },
            {
              "name": "isRoleDeniedError",
              "signature": "(error: unknown) => error is RoleDeniedError",
              "description": "Type guard to check if an error is a RoleDeniedError"
            }
          ],
          "types": [
            {
              "name": "RoleDeniedError",
              "description": "Error with user and role information for handling access denial",
              "properties": [
                {
                  "name": "name",
                  "type": "'RoleDeniedError'",
                  "required": true,
                  "description": "Error name for identification"
                },
                {
                  "name": "user",
                  "type": "User",
                  "required": true,
                  "description": "The authenticated user (allows logging before redirect)"
                },
                {
                  "name": "requiredRoles",
                  "type": "string[]",
                  "required": true,
                  "description": "The roles that were required (user has none of these)"
                },
                {
                  "name": "redirectTo",
                  "type": "string",
                  "required": false,
                  "description": "Optional redirect path for convenience"
                },
                {
                  "name": "message",
                  "type": "string",
                  "required": true,
                  "description": "Human-readable error message"
                }
              ]
            }
          ]
        },
        "user-utils": {
          "description": "Helper functions for displaying user information in the UI",
          "functions": [
            {
              "name": "getDisplayName",
              "signature": "(user: User | null | undefined) => string",
              "description": "Get a display-friendly name for a user. Priority: full name > first name > username > email > 'Unknown'",
              "example": "import { getDisplayName } from '@classic-homes/auth/core';\n\nconst name = getDisplayName(user);\n// Returns: \"John Doe\", \"John\", \"johndoe\", or \"john@example.com\""
            },
            {
              "name": "getUserInitials",
              "signature": "(user: User | null | undefined, maxLength?: number) => string",
              "description": "Get initials from a user's name for avatar fallbacks",
              "example": "import { getUserInitials } from '@classic-homes/auth/core';\n\nconst initials = getUserInitials(user);\n// Returns: \"JD\" for \"John Doe\", \"J\" for \"John\", \"?\" for unknown"
            },
            {
              "name": "getAvatarFallback",
              "signature": "(user: User | null | undefined) => string",
              "description": "Get avatar fallback text for a user. Alias for getUserInitials for semantic clarity.",
              "example": "import { getAvatarFallback } from '@classic-homes/auth/core';\n\n<Avatar>\n  <AvatarFallback>{getAvatarFallback(user)}</AvatarFallback>\n</Avatar>"
            },
            {
              "name": "getUserEmail",
              "signature": "(user: User | null | undefined, masked?: boolean) => string",
              "description": "Get the user's primary email, optionally masked for privacy",
              "example": "import { getUserEmail } from '@classic-homes/auth/core';\n\ngetUserEmail(user);        // \"john@example.com\"\ngetUserEmail(user, true);  // \"j***@example.com\""
            },
            {
              "name": "getGreeting",
              "signature": "(user: User | null | undefined, includeTime?: boolean) => string",
              "description": "Get a greeting for the user based on time of day",
              "example": "import { getGreeting } from '@classic-homes/auth/core';\n\ngetGreeting(user);             // \"Good morning, John\"\ngetGreeting(user, false);      // \"Hello, John\""
            },
            {
              "name": "formatUserRoles",
              "signature": "(user: User | null | undefined, options?: FormatUserRolesOptions) => string",
              "description": "Format user roles for display with customizable formatting",
              "example": "import { formatUserRoles } from '@classic-homes/auth/core';\n\nformatUserRoles(user);                    // \"Admin, Manager\"\nformatUserRoles(user, { max: 1 });        // \"Admin +1 more\"\nformatUserRoles(user, { lowercase: true }); // \"admin, manager\""
            }
          ],
          "types": [
            {
              "name": "FormatUserRolesOptions",
              "description": "Options for formatUserRoles function",
              "properties": [
                {
                  "name": "max",
                  "type": "number",
                  "required": false,
                  "description": "Maximum roles to show before '+N more'"
                },
                {
                  "name": "separator",
                  "type": "string",
                  "required": false,
                  "description": "Separator between roles (default: ', ')"
                },
                {
                  "name": "lowercase",
                  "type": "boolean",
                  "required": false,
                  "description": "Convert to lowercase (default: false)"
                },
                {
                  "name": "capitalize",
                  "type": "boolean",
                  "required": false,
                  "description": "Capitalize first letter of each role (default: true)"
                }
              ]
            }
          ]
        }
      },
      "types": [
        {
          "name": "User",
          "description": "User information",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "User ID"
            },
            {
              "name": "username",
              "type": "string",
              "required": true,
              "description": "Username"
            },
            {
              "name": "email",
              "type": "string",
              "required": true,
              "description": "Email address"
            },
            {
              "name": "firstName",
              "type": "string",
              "required": false,
              "description": "First name"
            },
            {
              "name": "lastName",
              "type": "string",
              "required": false,
              "description": "Last name"
            },
            {
              "name": "phone",
              "type": "string",
              "required": false,
              "description": "Phone number"
            },
            {
              "name": "role",
              "type": "string",
              "required": true,
              "description": "Primary role"
            },
            {
              "name": "roles",
              "type": "string[]",
              "required": false,
              "description": "All assigned roles"
            },
            {
              "name": "permissions",
              "type": "string[]",
              "required": true,
              "description": "User permissions"
            },
            {
              "name": "isActive",
              "type": "boolean",
              "required": true,
              "description": "Whether user is active"
            },
            {
              "name": "emailVerified",
              "type": "boolean",
              "required": true,
              "description": "Whether email is verified"
            },
            {
              "name": "authMethod",
              "type": "'password' | 'oauth' | 'both'",
              "required": false,
              "description": "Authentication method"
            },
            {
              "name": "ssoProfileUrl",
              "type": "string",
              "required": false,
              "description": "SSO profile URL"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            },
            {
              "name": "lastLoginAt",
              "type": "string",
              "required": false,
              "description": "Last login timestamp"
            }
          ]
        },
        {
          "name": "AuthState",
          "description": "Current authentication state",
          "properties": [
            {
              "name": "accessToken",
              "type": "string | null",
              "required": true,
              "description": "Current access token"
            },
            {
              "name": "refreshToken",
              "type": "string | null",
              "required": true,
              "description": "Current refresh token"
            },
            {
              "name": "user",
              "type": "User | null",
              "required": true,
              "description": "Current user"
            },
            {
              "name": "isAuthenticated",
              "type": "boolean",
              "required": true,
              "description": "Whether user is authenticated"
            }
          ]
        },
        {
          "name": "LoginCredentials",
          "description": "Login credentials",
          "properties": [
            {
              "name": "username",
              "type": "string",
              "required": true,
              "description": "Username or email"
            },
            {
              "name": "password",
              "type": "string",
              "required": true,
              "description": "Password"
            }
          ]
        },
        {
          "name": "LoginResponse",
          "description": "Response from login API",
          "properties": [
            {
              "name": "accessToken",
              "type": "string",
              "required": true,
              "description": "Access token"
            },
            {
              "name": "refreshToken",
              "type": "string",
              "required": true,
              "description": "Refresh token"
            },
            {
              "name": "sessionToken",
              "type": "string",
              "required": false,
              "description": "Session token"
            },
            {
              "name": "user",
              "type": "User",
              "required": true,
              "description": "User info"
            },
            {
              "name": "expiresIn",
              "type": "number",
              "required": true,
              "description": "Token expiration in seconds"
            },
            {
              "name": "requiresMFA",
              "type": "boolean",
              "required": false,
              "description": "Whether MFA is required"
            },
            {
              "name": "mfaToken",
              "type": "string",
              "required": false,
              "description": "MFA challenge token"
            },
            {
              "name": "availableMethods",
              "type": "string[]",
              "required": false,
              "description": "Available MFA methods"
            }
          ]
        },
        {
          "name": "Session",
          "description": "User session information",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Session ID"
            },
            {
              "name": "deviceName",
              "type": "string",
              "required": true,
              "description": "Device name"
            },
            {
              "name": "browser",
              "type": "string",
              "required": true,
              "description": "Browser info"
            },
            {
              "name": "location",
              "type": "string",
              "required": true,
              "description": "Location"
            },
            {
              "name": "ipAddress",
              "type": "string",
              "required": true,
              "description": "IP address"
            },
            {
              "name": "lastActivity",
              "type": "string",
              "required": true,
              "description": "Last activity timestamp"
            },
            {
              "name": "isCurrent",
              "type": "boolean",
              "required": true,
              "description": "Whether this is the current session"
            },
            {
              "name": "isTrusted",
              "type": "boolean",
              "required": true,
              "description": "Whether device is trusted"
            },
            {
              "name": "deviceFingerprint",
              "type": "string",
              "required": true,
              "description": "Device fingerprint"
            },
            {
              "name": "trustedDeviceId",
              "type": "string",
              "required": false,
              "description": "Trusted device ID if device is trusted"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Session creation time"
            },
            {
              "name": "expiresAt",
              "type": "string",
              "required": true,
              "description": "Session expiration time"
            }
          ]
        },
        {
          "name": "Device",
          "description": "Device information",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Device ID"
            },
            {
              "name": "deviceFingerprint",
              "type": "string",
              "required": true,
              "description": "Device fingerprint"
            },
            {
              "name": "deviceName",
              "type": "string",
              "required": true,
              "description": "Device name"
            },
            {
              "name": "userAgent",
              "type": "string | null",
              "required": false,
              "description": "User agent string"
            },
            {
              "name": "ipAddress",
              "type": "string | null",
              "required": false,
              "description": "IP address"
            },
            {
              "name": "trusted",
              "type": "boolean",
              "required": true,
              "description": "Whether device is trusted"
            },
            {
              "name": "status",
              "type": "'active' | 'revoked'",
              "required": true,
              "description": "Device status"
            },
            {
              "name": "lastSeen",
              "type": "string | null",
              "required": false,
              "description": "Last seen timestamp"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            }
          ]
        },
        {
          "name": "ApiKey",
          "description": "API key information",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "API key ID"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Key name"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Key description"
            },
            {
              "name": "keyPreview",
              "type": "string",
              "required": true,
              "description": "Partial key preview"
            },
            {
              "name": "permissions",
              "type": "string[]",
              "required": true,
              "description": "Key permissions"
            },
            {
              "name": "role",
              "type": "string | null",
              "required": false,
              "description": "Role assigned to key"
            },
            {
              "name": "isActive",
              "type": "boolean",
              "required": true,
              "description": "Whether key is active"
            },
            {
              "name": "expiresAt",
              "type": "string | null",
              "required": false,
              "description": "Expiration date"
            },
            {
              "name": "lastUsedAt",
              "type": "string | null",
              "required": false,
              "description": "Last used timestamp"
            },
            {
              "name": "usageCount",
              "type": "number",
              "required": false,
              "description": "Number of times key has been used"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            }
          ]
        },
        {
          "name": "MFASetupResponse",
          "description": "MFA setup response with QR code and backup codes",
          "properties": [
            {
              "name": "qrCodeUrl",
              "type": "string",
              "required": true,
              "description": "QR code image URL"
            },
            {
              "name": "manualEntryKey",
              "type": "string",
              "required": true,
              "description": "Manual entry key for authenticator apps"
            },
            {
              "name": "backupCodes",
              "type": "string[]",
              "required": true,
              "description": "One-time backup codes"
            },
            {
              "name": "instructions",
              "type": "object",
              "required": true,
              "description": "Setup instructions"
            }
          ]
        },
        {
          "name": "MFAStatus",
          "description": "MFA status for user",
          "properties": [
            {
              "name": "enabled",
              "type": "boolean",
              "required": true,
              "description": "Whether MFA is enabled"
            },
            {
              "name": "methods",
              "type": "string[]",
              "required": true,
              "description": "Enabled MFA methods"
            }
          ]
        },
        {
          "name": "MFAChallengeData",
          "description": "Data for MFA challenge verification",
          "properties": [
            {
              "name": "mfaToken",
              "type": "string",
              "required": true,
              "description": "MFA challenge token from login"
            },
            {
              "name": "code",
              "type": "string",
              "required": true,
              "description": "MFA code from authenticator"
            },
            {
              "name": "method",
              "type": "'totp' | 'email' | 'backup'",
              "required": false,
              "description": "MFA method"
            },
            {
              "name": "trustDevice",
              "type": "boolean",
              "required": false,
              "description": "Whether to trust this device"
            }
          ]
        },
        {
          "name": "UserPreferences",
          "description": "User preference settings",
          "properties": [
            {
              "name": "emailNotifications",
              "type": "boolean",
              "required": true,
              "description": "Email notifications enabled"
            },
            {
              "name": "securityAlerts",
              "type": "boolean",
              "required": true,
              "description": "Security alerts enabled"
            },
            {
              "name": "loginNotifications",
              "type": "boolean",
              "required": true,
              "description": "Login notifications enabled"
            },
            {
              "name": "suspiciousActivityAlerts",
              "type": "boolean",
              "required": true,
              "description": "Suspicious activity alerts"
            },
            {
              "name": "passwordChangeNotifications",
              "type": "boolean",
              "required": true,
              "description": "Password change notifications"
            },
            {
              "name": "mfaChangeNotifications",
              "type": "boolean",
              "required": true,
              "description": "MFA change notifications"
            },
            {
              "name": "accountUpdates",
              "type": "boolean",
              "required": true,
              "description": "Account updates enabled"
            },
            {
              "name": "systemMaintenance",
              "type": "boolean",
              "required": true,
              "description": "System maintenance notifications"
            },
            {
              "name": "featureAnnouncements",
              "type": "boolean",
              "required": true,
              "description": "Feature announcements"
            },
            {
              "name": "newsAndUpdates",
              "type": "boolean",
              "required": true,
              "description": "News and updates"
            },
            {
              "name": "apiKeyExpiration",
              "type": "boolean",
              "required": true,
              "description": "API key expiration alerts"
            },
            {
              "name": "apiUsageAlerts",
              "type": "boolean",
              "required": true,
              "description": "API usage alerts"
            },
            {
              "name": "rateLimit",
              "type": "boolean",
              "required": true,
              "description": "Rate limit notifications"
            },
            {
              "name": "dataExportReady",
              "type": "boolean",
              "required": true,
              "description": "Data export ready notifications"
            },
            {
              "name": "reportGeneration",
              "type": "boolean",
              "required": true,
              "description": "Report generation notifications"
            },
            {
              "name": "theme",
              "type": "'light' | 'dark' | 'system'",
              "required": true,
              "description": "UI theme preference"
            },
            {
              "name": "language",
              "type": "string",
              "required": true,
              "description": "Language preference"
            },
            {
              "name": "timezone",
              "type": "string",
              "required": true,
              "description": "Timezone preference"
            },
            {
              "name": "dateFormat",
              "type": "string",
              "required": true,
              "description": "Date format preference"
            },
            {
              "name": "timeFormat",
              "type": "'12h' | '24h'",
              "required": true,
              "description": "Time format preference"
            },
            {
              "name": "advancedSettings",
              "type": "Record<string, any> | null",
              "required": false,
              "description": "Advanced settings"
            },
            {
              "name": "lastUpdated",
              "type": "string",
              "required": false,
              "description": "Timestamp of last update"
            }
          ]
        },
        {
          "name": "LinkedAccount",
          "description": "SSO linked account",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Account ID"
            },
            {
              "name": "provider",
              "type": "string",
              "required": true,
              "description": "SSO provider name"
            },
            {
              "name": "providerAccountId",
              "type": "string",
              "required": true,
              "description": "Provider account ID"
            },
            {
              "name": "providerEmail",
              "type": "string | null",
              "required": false,
              "description": "Provider email"
            },
            {
              "name": "providerUsername",
              "type": "string | null",
              "required": false,
              "description": "Provider username"
            },
            {
              "name": "providerAvatarUrl",
              "type": "string | null",
              "required": false,
              "description": "Provider avatar URL"
            },
            {
              "name": "isPrimary",
              "type": "boolean",
              "required": true,
              "description": "Whether this is primary account"
            },
            {
              "name": "isVerified",
              "type": "boolean",
              "required": true,
              "description": "Whether account is verified"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Link creation time"
            },
            {
              "name": "lastUsedAt",
              "type": "string | null",
              "required": false,
              "description": "Last used timestamp"
            }
          ]
        },
        {
          "name": "SecurityEvent",
          "description": "Security event log entry",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Event ID"
            },
            {
              "name": "eventType",
              "type": "string",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "type",
              "type": "string",
              "required": false,
              "description": "Event type (deprecated, use eventType)"
            },
            {
              "name": "description",
              "type": "string",
              "required": true,
              "description": "Event description"
            },
            {
              "name": "severity",
              "type": "'low' | 'medium' | 'high'",
              "required": true,
              "description": "Event severity"
            },
            {
              "name": "ipAddress",
              "type": "string",
              "required": false,
              "description": "Source IP address"
            },
            {
              "name": "userAgent",
              "type": "string",
              "required": false,
              "description": "User agent string"
            },
            {
              "name": "location",
              "type": "string",
              "required": false,
              "description": "Location of event"
            },
            {
              "name": "metadata",
              "type": "Record<string, unknown>",
              "required": false,
              "description": "Additional metadata"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Event timestamp"
            }
          ]
        },
        {
          "name": "LogoutResponse",
          "description": "Response from logout API",
          "properties": [
            {
              "name": "success",
              "type": "boolean",
              "required": true,
              "description": "Whether logout was successful"
            },
            {
              "name": "message",
              "type": "string",
              "required": false,
              "description": "Logout message"
            },
            {
              "name": "ssoLogout",
              "type": "boolean",
              "required": false,
              "description": "Whether SSO logout is required"
            },
            {
              "name": "logoutUrl",
              "type": "string",
              "required": false,
              "description": "SSO logout URL if applicable"
            }
          ]
        },
        {
          "name": "RegisterResponse",
          "description": "Response from register API",
          "properties": [
            {
              "name": "message",
              "type": "string",
              "required": true,
              "description": "Registration message"
            },
            {
              "name": "user",
              "type": "User",
              "required": true,
              "description": "Created user"
            },
            {
              "name": "requiresEmailVerification",
              "type": "boolean",
              "required": false,
              "description": "Whether email verification is required"
            }
          ]
        },
        {
          "name": "RegisterData",
          "description": "Data for user registration",
          "properties": [
            {
              "name": "username",
              "type": "string",
              "required": true,
              "description": "Username"
            },
            {
              "name": "email",
              "type": "string",
              "required": true,
              "description": "Email address"
            },
            {
              "name": "password",
              "type": "string",
              "required": true,
              "description": "Password"
            },
            {
              "name": "firstName",
              "type": "string",
              "required": false,
              "description": "First name"
            },
            {
              "name": "lastName",
              "type": "string",
              "required": false,
              "description": "Last name"
            }
          ]
        },
        {
          "name": "CreateApiKeyRequest",
          "description": "Request to create an API key",
          "properties": [
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Key name"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Key description"
            },
            {
              "name": "permissions",
              "type": "string[]",
              "required": false,
              "description": "Key permissions"
            },
            {
              "name": "role",
              "type": "string",
              "required": false,
              "description": "Role to assign"
            },
            {
              "name": "expiresAt",
              "type": "string | null",
              "required": false,
              "description": "Expiration date"
            }
          ]
        },
        {
          "name": "CreateApiKeyResponse",
          "description": "Response from creating an API key (includes full key)",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Key ID"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Key name"
            },
            {
              "name": "key",
              "type": "string",
              "required": true,
              "description": "Full API key (only returned once)"
            },
            {
              "name": "keyPreview",
              "type": "string",
              "required": true,
              "description": "Partial key preview"
            },
            {
              "name": "permissions",
              "type": "string[]",
              "required": true,
              "description": "Key permissions"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            }
          ]
        },
        {
          "name": "ProfileUpdateData",
          "description": "Data for updating user profile",
          "properties": [
            {
              "name": "firstName",
              "type": "string",
              "required": false,
              "description": "First name"
            },
            {
              "name": "lastName",
              "type": "string",
              "required": false,
              "description": "Last name"
            },
            {
              "name": "email",
              "type": "string",
              "required": false,
              "description": "Email address"
            },
            {
              "name": "phone",
              "type": "string",
              "required": false,
              "description": "Phone number"
            }
          ]
        },
        {
          "name": "ChangePasswordData",
          "description": "Data for changing password",
          "properties": [
            {
              "name": "currentPassword",
              "type": "string",
              "required": true,
              "description": "Current password"
            },
            {
              "name": "newPassword",
              "type": "string",
              "required": true,
              "description": "New password"
            }
          ]
        },
        {
          "name": "ResetPasswordData",
          "description": "Data for resetting password",
          "properties": [
            {
              "name": "token",
              "type": "string",
              "required": true,
              "description": "Reset token"
            },
            {
              "name": "newPassword",
              "type": "string",
              "required": true,
              "description": "New password"
            }
          ]
        },
        {
          "name": "Pagination",
          "description": "Pagination information",
          "properties": [
            {
              "name": "page",
              "type": "number",
              "required": true,
              "description": "Current page number"
            },
            {
              "name": "limit",
              "type": "number",
              "required": true,
              "description": "Items per page"
            },
            {
              "name": "total",
              "type": "number",
              "required": true,
              "description": "Total items"
            },
            {
              "name": "totalPages",
              "type": "number",
              "required": true,
              "description": "Total pages"
            }
          ]
        }
      ]
    },
    "svelte": {
      "importPath": "@classic-homes/auth/svelte",
      "description": "Svelte-specific bindings for authentication. Includes reactive stores and route guards.",
      "modules": {
        "stores": {
          "description": "Reactive Svelte stores for auth state",
          "functions": [
            {
              "name": "authStore",
              "signature": "Writable<AuthState>",
              "description": "Main auth store containing current auth state",
              "example": "import { authStore } from '@classic-homes/auth/svelte';\n\n// Subscribe to auth state changes\nauthStore.subscribe(state => {\n  console.log('Authenticated:', state.isAuthenticated);\n  console.log('User:', state.user);\n});\n\n// In Svelte component\n<script>\n  import { authStore } from '@classic-homes/auth/svelte';\n</script>\n\n{#if $authStore.isAuthenticated}\n  <p>Welcome, {$authStore.user.firstName}!</p>\n{/if}"
            },
            {
              "name": "isAuthenticated",
              "signature": "Readable<boolean>",
              "description": "Derived store for authentication status",
              "example": "import { isAuthenticated } from '@classic-homes/auth/svelte';\n\n// In Svelte component\n{#if $isAuthenticated}\n  <Dashboard />\n{:else}\n  <LoginForm />\n{/if}"
            },
            {
              "name": "currentUser",
              "signature": "Readable<User | null>",
              "description": "Derived store for current user"
            },
            {
              "name": "authActions",
              "signature": "{ setAuth, updateTokens, updateUser, logout, logoutWithSSO, handleSessionExpired, hasPermission, hasRole, hasAnyRole, hasAllRoles, hasAnyPermission, hasAllPermissions, rehydrate, reset }",
              "description": "Auth action functions that update the store and check permissions",
              "example": "import { authActions } from '@classic-homes/auth/svelte';\n\n// After successful login, set the auth state\nauthActions.setAuth(accessToken, refreshToken, user, sessionToken);\n\n// Update tokens after refresh\nauthActions.updateTokens(newAccessToken, newRefreshToken);\n\n// Update user data\nauthActions.updateUser(updatedUser);\n\n// Logout (clears local state)\nauthActions.logout();\n\n// Logout with SSO (calls API and clears state)\nconst { ssoLogoutUrl } = await authActions.logoutWithSSO();\nif (ssoLogoutUrl) window.location.href = ssoLogoutUrl;\n\n// Handle session expiration (triggers onSessionExpired callback)\nauthActions.handleSessionExpired();\n\n// Permission/role checks\nif (authActions.hasRole('admin')) { /* admin logic */ }\nif (authActions.hasPermission('users:write')) { /* write logic */ }\n\n// Reset store for testing (clears state, storage, and subscribers)\nauthActions.reset();"
            }
          ]
        },
        "guards": {
          "description": "SvelteKit route guards for protected pages",
          "functions": [
            {
              "name": "checkAuth",
              "signature": "(options?: AuthGuardOptions) => AuthGuardResult",
              "description": "Check authentication status with optional role/permission requirements",
              "example": "import { checkAuth } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\nexport function load() {\n  const result = checkAuth({ roles: ['admin'] });\n  if (!result.allowed) {\n    throw redirect(302, result.redirectTo ?? '/login');\n  }\n}"
            },
            {
              "name": "requireAuth",
              "signature": "() => AuthGuardResult",
              "description": "Simple authentication check (just checks if authenticated)",
              "example": "import { requireAuth } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\nexport function load() {\n  const result = requireAuth();\n  if (!result.allowed) {\n    throw redirect(302, result.redirectTo ?? '/login');\n  }\n}"
            },
            {
              "name": "requireRole",
              "signature": "(roles: string | string[], requireAll?: boolean) => AuthGuardResult",
              "description": "Check if user has specific role(s)",
              "example": "import { requireRole } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\nexport function load() {\n  // Single role\n  const result = requireRole('admin');\n  \n  // Multiple roles (any match by default)\n  const result = requireRole(['admin', 'manager']);\n  \n  // Require all roles\n  const result = requireRole(['admin', 'manager'], true);\n  \n  if (!result.allowed) {\n    throw redirect(302, result.redirectTo ?? '/unauthorized');\n  }\n}"
            },
            {
              "name": "requirePermission",
              "signature": "(permissions: string | string[], requireAll?: boolean) => AuthGuardResult",
              "description": "Check if user has specific permission(s)",
              "example": "import { requirePermission } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\nexport function load() {\n  const result = requirePermission('users:write');\n  if (!result.allowed) {\n    throw redirect(302, result.redirectTo ?? '/unauthorized');\n  }\n}"
            },
            {
              "name": "protectedLoad",
              "signature": "<T>(options: AuthGuardOptions, loadFn: (event: { fetch: typeof fetch }) => Promise<T>) => (event: { fetch: typeof fetch }) => Promise<T | { redirect: string }>",
              "description": "Wrapper for load functions that require auth",
              "example": "import { protectedLoad } from '@classic-homes/auth/svelte';\n\nexport const load = protectedLoad(\n  { roles: ['admin'] },\n  async ({ fetch }) => {\n    // This only runs if auth check passes\n    const data = await fetch('/api/admin/data');\n    return { data };\n  }\n);"
            },
            {
              "name": "createAuthGuard",
              "signature": "(options?: AuthGuardOptions) => (onDenied: (redirectTo: string, reason: string) => void) => boolean",
              "description": "Create a guard function for use in SvelteKit load functions",
              "example": "import { createAuthGuard } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\nconst requireAdmin = createAuthGuard({ roles: ['admin'] });\n\nexport function load() {\n  requireAdmin((redirectTo) => {\n    throw redirect(302, redirectTo);\n  });\n  // Continue with protected logic\n}"
            }
          ],
          "types": [
            {
              "name": "AuthGuardOptions",
              "description": "Options for auth guards",
              "properties": [
                {
                  "name": "roles",
                  "type": "string[]",
                  "required": false,
                  "description": "Required roles (user must have at least one by default)"
                },
                {
                  "name": "permissions",
                  "type": "string[]",
                  "required": false,
                  "description": "Required permissions (user must have at least one by default)"
                },
                {
                  "name": "requireAllRoles",
                  "type": "boolean",
                  "required": false,
                  "description": "Require all specified roles (default: false)"
                },
                {
                  "name": "requireAllPermissions",
                  "type": "boolean",
                  "required": false,
                  "description": "Require all specified permissions (default: false)"
                }
              ]
            },
            {
              "name": "AuthGuardResult",
              "description": "Result from auth guard check",
              "properties": [
                {
                  "name": "allowed",
                  "type": "boolean",
                  "required": true,
                  "description": "Whether the user is allowed"
                },
                {
                  "name": "reason",
                  "type": "'not_authenticated' | 'missing_role' | 'missing_permission'",
                  "required": false,
                  "description": "Reason for denial (if not allowed)"
                },
                {
                  "name": "redirectTo",
                  "type": "string",
                  "required": false,
                  "description": "Redirect URL suggestion"
                }
              ]
            }
          ]
        },
        "client": {
          "description": "Simplified auth client factory for one-liner setup",
          "functions": [
            {
              "name": "createAuthClient",
              "signature": "(options: CreateAuthClientOptions) => AuthClient",
              "description": "Create and initialize the auth client with stores and services bundled",
              "example": "import { createAuthClient } from '@classic-homes/auth/svelte';\nimport { PUBLIC_API_URL } from '$env/static/public';\n\n// In your lib/auth.ts\nexport const {\n  authStore,\n  authActions,\n  authService,\n  isAuthenticated,\n  currentUser,\n} = createAuthClient({\n  baseUrl: PUBLIC_API_URL,\n  storageKey: 'my_app_auth',\n  sso: { enabled: true, provider: 'authentik' },\n  onSessionExpired: (currentPath) => {\n    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;\n  },\n});\n\n// In Svelte component\nimport { isAuthenticated, currentUser } from '$lib/auth';\n\n{#if $isAuthenticated}\n  <p>Welcome, {$currentUser?.firstName}</p>\n{/if}"
            },
            {
              "name": "initAuth",
              "signature": "(config: AuthConfig) => void",
              "description": "Re-exported from core for cases where you need more control"
            },
            {
              "name": "isInitialized",
              "signature": "() => boolean",
              "description": "Re-exported from core to check if auth has been initialized"
            }
          ],
          "types": [
            {
              "name": "CreateAuthClientOptions",
              "description": "Options for createAuthClient, extending AuthConfig",
              "properties": [
                {
                  "name": "baseUrl",
                  "type": "string",
                  "required": true,
                  "description": "Base URL for the API"
                },
                {
                  "name": "storageKey",
                  "type": "string",
                  "required": false,
                  "description": "Storage key prefix for auth data"
                },
                {
                  "name": "autoRehydrate",
                  "type": "boolean",
                  "required": false,
                  "description": "Auto-rehydrate auth state from storage on creation (default: true)"
                },
                {
                  "name": "sso",
                  "type": "SSOConfig",
                  "required": false,
                  "description": "SSO configuration"
                },
                {
                  "name": "onSessionExpired",
                  "type": "(currentPath: string) => void",
                  "required": false,
                  "description": "Callback when session expires"
                },
                {
                  "name": "onAuthError",
                  "type": "(error: Error) => void",
                  "required": false,
                  "description": "Callback for auth errors"
                },
                {
                  "name": "onLogout",
                  "type": "() => void",
                  "required": false,
                  "description": "Callback when user logs out"
                }
              ]
            },
            {
              "name": "AuthClient",
              "description": "Return type from createAuthClient with all auth utilities",
              "properties": [
                {
                  "name": "authStore",
                  "type": "AuthStore",
                  "required": true,
                  "description": "The auth store (Svelte store contract)"
                },
                {
                  "name": "authActions",
                  "type": "AuthActions",
                  "required": true,
                  "description": "Actions for modifying auth state"
                },
                {
                  "name": "authService",
                  "type": "AuthService",
                  "required": true,
                  "description": "Service for API calls"
                },
                {
                  "name": "isAuthenticated",
                  "type": "Readable<boolean>",
                  "required": true,
                  "description": "Derived store for isAuthenticated"
                },
                {
                  "name": "currentUser",
                  "type": "Readable<User | null>",
                  "required": true,
                  "description": "Derived store for currentUser"
                }
              ]
            }
          ]
        },
        "hooks": {
          "description": "Server-side authentication middleware for SvelteKit hooks.server.ts",
          "functions": [
            {
              "name": "createAuthHook",
              "signature": "(options?: AuthHookOptions) => Handle",
              "description": "Create a SvelteKit handle function for server-side authentication",
              "example": "// src/hooks.server.ts\nimport { createAuthHook } from '@classic-homes/auth/svelte';\nimport { sequence } from '@sveltejs/kit/hooks';\n\nconst authHook = createAuthHook({\n  loginPath: '/auth/login',\n  publicRoutes: [/^\\/auth\\//, /^\\/api\\/health/],\n  cookieName: 'my_app_auth',\n});\n\nexport const handle = sequence(authHook);"
            },
            {
              "name": "matchesRoute",
              "signature": "(pathname: string, patterns: RegExp[]) => boolean",
              "description": "Helper to check if a pathname matches any pattern in a list"
            },
            {
              "name": "routePatterns",
              "signature": "{ auth, api, apiAuth, public, admin, root, health, prefix, exact }",
              "description": "Pre-built route patterns for common use cases",
              "example": "import { createAuthHook, routePatterns } from '@classic-homes/auth/svelte';\n\nconst authHook = createAuthHook({\n  publicRoutes: [\n    routePatterns.auth,       // /auth/*\n    routePatterns.health,     // /health, /healthz, /ready, /live\n    routePatterns.exact('/about'),  // exact /about\n    routePatterns.prefix('/public'), // /public/*\n  ],\n  protectedRoutes: [\n    routePatterns.admin,      // /admin/*\n    routePatterns.api,        // /api/*\n  ],\n});"
            }
          ],
          "types": [
            {
              "name": "AuthHookOptions",
              "description": "Configuration options for the auth hook",
              "properties": [
                {
                  "name": "loginPath",
                  "type": "string",
                  "required": false,
                  "description": "Path to redirect unauthenticated users (default: '/login')"
                },
                {
                  "name": "protectedRoutes",
                  "type": "RegExp[]",
                  "required": false,
                  "description": "Routes that require authentication. If set, only these routes need auth."
                },
                {
                  "name": "publicRoutes",
                  "type": "RegExp[]",
                  "required": false,
                  "description": "Routes that don't require auth (default: [/^\\/auth\\//, /^\\/api\\/auth\\//])"
                },
                {
                  "name": "cookieName",
                  "type": "string",
                  "required": false,
                  "description": "Cookie name for auth token (default: 'classic_auth')"
                },
                {
                  "name": "headerName",
                  "type": "string",
                  "required": false,
                  "description": "Alternative header name to check for auth token"
                },
                {
                  "name": "redirectParam",
                  "type": "string",
                  "required": false,
                  "description": "Query parameter for redirect URL (default: 'redirect')"
                },
                {
                  "name": "isAuthenticated",
                  "type": "(event: RequestEvent) => boolean | Promise<boolean>",
                  "required": false,
                  "description": "Custom function to determine if request is authenticated"
                },
                {
                  "name": "onUnauthenticated",
                  "type": "(event: RequestEvent) => Response | Promise<Response>",
                  "required": false,
                  "description": "Custom handler for unauthenticated requests"
                },
                {
                  "name": "onAuthCheck",
                  "type": "(event: RequestEvent, isAuthenticated: boolean) => void",
                  "required": false,
                  "description": "Callback for auth check logging/analytics"
                }
              ]
            }
          ]
        },
        "utils": {
          "description": "Navigation filtering utilities based on user roles and permissions",
          "functions": [
            {
              "name": "filterByAccess",
              "signature": "<T extends RoleRestrictedItem>(items: T[], user: User | null, options?: NavFilterOptions) => T[]",
              "description": "Filter navigation items based on user roles and permissions",
              "example": "import { filterByAccess } from '@classic-homes/auth/svelte';\n\nconst navItems = [\n  { id: 'dashboard', label: 'Dashboard' },\n  { id: 'users', label: 'Users', roles: ['admin', 'manager'] },\n  { id: 'settings', label: 'Settings', permissions: ['settings:read'] },\n];\n\nconst filtered = filterByAccess(navItems, user);\n// Returns items the user can see based on their roles/permissions"
            },
            {
              "name": "filterNavSections",
              "signature": "<TItem, TSection>(sections: TSection[], user: User | null, options?: NavFilterOptions) => TSection[]",
              "description": "Filter navigation sections with nested items, removing empty sections",
              "example": "import { filterNavSections } from '@classic-homes/auth/svelte';\n\nconst sections = [\n  {\n    title: 'Main',\n    items: [\n      { id: 'dashboard', label: 'Dashboard' },\n      { id: 'analytics', label: 'Analytics', roles: ['admin'] },\n    ],\n  },\n  {\n    title: 'Admin',\n    items: [\n      { id: 'users', label: 'Users', roles: ['admin'] },\n    ],\n  },\n];\n\nconst filtered = filterNavSections(sections, user);\n// Admin section removed entirely if user isn't admin"
            },
            {
              "name": "canAccess",
              "signature": "(item: RoleRestrictedItem, user: User | null, options?: NavFilterOptions) => boolean",
              "description": "Check if a user can access a specific item",
              "example": "import { canAccess } from '@classic-homes/auth/svelte';\n\nconst adminItem = { id: 'admin', roles: ['admin'] };\n\nif (canAccess(adminItem, user)) {\n  // Show admin link\n}"
            },
            {
              "name": "createNavFilter",
              "signature": "(defaultOptions?: NavFilterOptions) => FilterFunction",
              "description": "Create a pre-configured filter function with fixed options",
              "example": "import { createNavFilter } from '@classic-homes/auth/svelte';\n\n// In a config file\nexport const filterNav = createNavFilter({ requireAllRoles: false });\n\n// In components\nconst filtered = filterNav(items, user);"
            }
          ],
          "types": [
            {
              "name": "RoleRestrictedItem",
              "description": "Interface for items that can be filtered by role/permission",
              "properties": [
                {
                  "name": "id",
                  "type": "string",
                  "required": false,
                  "description": "Optional item identifier"
                },
                {
                  "name": "roles",
                  "type": "string[]",
                  "required": false,
                  "description": "Roles that can see this item (any by default)"
                },
                {
                  "name": "permissions",
                  "type": "string[]",
                  "required": false,
                  "description": "Permissions that can see this item (any by default)"
                }
              ]
            },
            {
              "name": "NavFilterOptions",
              "description": "Options for navigation filtering behavior",
              "properties": [
                {
                  "name": "requireAllRoles",
                  "type": "boolean",
                  "required": false,
                  "description": "Require ALL specified roles (default: false = any role)"
                },
                {
                  "name": "requireAllPermissions",
                  "type": "boolean",
                  "required": false,
                  "description": "Require ALL specified permissions (default: false = any permission)"
                },
                {
                  "name": "hideUnrestrictedForGuests",
                  "type": "boolean",
                  "required": false,
                  "description": "Hide unrestricted items for unauthenticated users (default: false)"
                }
              ]
            }
          ]
        }
      }
    },
    "testing": {
      "importPath": "@classic-homes/auth/testing",
      "description": "Test utilities for the auth package. Provides mocks, fixtures, and helpers for testing authentication flows.",
      "modules": {
        "fixtures": {
          "description": "Pre-built test fixtures for users, tokens, and responses",
          "functions": [
            {
              "name": "mockUser",
              "signature": "User",
              "description": "Standard test user fixture"
            },
            {
              "name": "mockAdminUser",
              "signature": "User",
              "description": "Admin user fixture with elevated permissions"
            },
            {
              "name": "mockSSOUser",
              "signature": "User",
              "description": "SSO-authenticated user fixture"
            },
            {
              "name": "mockMFAUser",
              "signature": "User",
              "description": "User with MFA enabled"
            },
            {
              "name": "mockUnverifiedUser",
              "signature": "User",
              "description": "User with unverified email"
            },
            {
              "name": "mockInactiveUser",
              "signature": "User",
              "description": "Inactive/disabled user"
            },
            {
              "name": "createMockUser",
              "signature": "(overrides?: Partial<User>) => User",
              "description": "Create a custom mock user"
            },
            {
              "name": "createMockUserWithRoles",
              "signature": "(roles: string[], permissions?: string[]) => User",
              "description": "Create a user with specific roles"
            },
            {
              "name": "createMockUserWithAuthMethod",
              "signature": "(authMethod: 'password' | 'oauth' | 'both') => User",
              "description": "Create a user with specific auth method"
            },
            {
              "name": "mockAccessToken",
              "signature": "string",
              "description": "Valid mock access token"
            },
            {
              "name": "mockRefreshToken",
              "signature": "string",
              "description": "Valid mock refresh token"
            },
            {
              "name": "mockExpiredToken",
              "signature": "string",
              "description": "Expired token for testing"
            },
            {
              "name": "mockAdminToken",
              "signature": "string",
              "description": "Admin access token"
            },
            {
              "name": "mockMFAToken",
              "signature": "string",
              "description": "MFA challenge token"
            },
            {
              "name": "mockSessionToken",
              "signature": "string",
              "description": "Session token"
            },
            {
              "name": "createMockJWT",
              "signature": "(payload?: JWTPayloadOptions) => string",
              "description": "Create a custom mock JWT"
            },
            {
              "name": "createExpiredMockJWT",
              "signature": "(payload?: JWTPayloadOptions) => string",
              "description": "Create an expired mock JWT"
            },
            {
              "name": "createMockTokenPair",
              "signature": "() => { accessToken: string; refreshToken: string }",
              "description": "Create matching access and refresh tokens"
            },
            {
              "name": "createMockMFAToken",
              "signature": "() => string",
              "description": "Create an MFA challenge token"
            },
            {
              "name": "mockLoginSuccess",
              "signature": "LoginResponse",
              "description": "Successful login response"
            },
            {
              "name": "mockMFARequired",
              "signature": "LoginResponse",
              "description": "Login response requiring MFA"
            },
            {
              "name": "mockMFARequiredLegacy",
              "signature": "LoginResponse",
              "description": "Login response requiring MFA (deprecated format)"
            },
            {
              "name": "mockAdminLoginSuccess",
              "signature": "LoginResponse",
              "description": "Admin login success response"
            },
            {
              "name": "mockSSOLoginSuccess",
              "signature": "LoginResponse",
              "description": "SSO login success response"
            },
            {
              "name": "mockLogoutSuccess",
              "signature": "LogoutResponse",
              "description": "Successful logout response"
            },
            {
              "name": "mockSSOLogoutResponse",
              "signature": "LogoutResponse",
              "description": "SSO logout response with redirect URL"
            },
            {
              "name": "mockRegisterSuccess",
              "signature": "RegisterResponse",
              "description": "Successful registration response"
            },
            {
              "name": "mockRegisterRequiresVerification",
              "signature": "RegisterResponse",
              "description": "Registration requiring email verification"
            },
            {
              "name": "mockMFASetup",
              "signature": "MFASetupResponse",
              "description": "MFA setup response with QR code"
            },
            {
              "name": "mockMFAStatusEnabled",
              "signature": "MFAStatus",
              "description": "MFA enabled status"
            },
            {
              "name": "mockMFAStatusDisabled",
              "signature": "MFAStatus",
              "description": "MFA disabled status"
            },
            {
              "name": "mockCurrentSession",
              "signature": "Session",
              "description": "Current session fixture"
            },
            {
              "name": "mockOtherSession",
              "signature": "Session",
              "description": "Non-current session fixture"
            },
            {
              "name": "mockSessions",
              "signature": "Session[]",
              "description": "Array of mock sessions"
            },
            {
              "name": "mockTrustedDevice",
              "signature": "Device",
              "description": "Trusted device fixture"
            },
            {
              "name": "mockUntrustedDevice",
              "signature": "Device",
              "description": "Untrusted device fixture"
            },
            {
              "name": "mockDevices",
              "signature": "Device[]",
              "description": "Array of mock devices"
            },
            {
              "name": "mockApiKey",
              "signature": "ApiKey",
              "description": "Single API key fixture"
            },
            {
              "name": "mockApiKeys",
              "signature": "ApiKey[]",
              "description": "Array of mock API keys"
            },
            {
              "name": "mockLinkedAccount",
              "signature": "LinkedAccount",
              "description": "SSO linked account fixture"
            },
            {
              "name": "mockSecurityEventLogin",
              "signature": "SecurityEvent",
              "description": "Login security event"
            },
            {
              "name": "mockSecurityEventPasswordChange",
              "signature": "SecurityEvent",
              "description": "Password change security event"
            },
            {
              "name": "mockSecurityEventSuspicious",
              "signature": "SecurityEvent",
              "description": "Suspicious activity security event"
            },
            {
              "name": "mockSecurityEvents",
              "signature": "SecurityEvent[]",
              "description": "Array of mock security events"
            },
            {
              "name": "mockUserPreferences",
              "signature": "UserPreferences",
              "description": "Mock user preferences"
            },
            {
              "name": "createMockLoginSuccess",
              "signature": "(overrides?: Partial<LoginResponse>) => LoginResponse",
              "description": "Create a custom login success response"
            },
            {
              "name": "createMockMFARequired",
              "signature": "(overrides?: Partial<LoginResponse>) => LoginResponse",
              "description": "Create a custom MFA required response"
            },
            {
              "name": "createMockSession",
              "signature": "(overrides?: Partial<Session>) => Session",
              "description": "Create a custom session fixture"
            },
            {
              "name": "createMockDevice",
              "signature": "(overrides?: Partial<Device>) => Device",
              "description": "Create a custom device fixture"
            },
            {
              "name": "createMockSecurityEvent",
              "signature": "(overrides?: Partial<SecurityEvent>) => SecurityEvent",
              "description": "Create a custom security event fixture"
            }
          ]
        },
        "mocks": {
          "description": "Mock implementations for testing",
          "functions": [
            {
              "name": "MockStorageAdapter",
              "signature": "class",
              "description": "Mock storage adapter with call tracking",
              "example": "import { MockStorageAdapter, createMockStorage } from '@classic-homes/auth/testing';\n\nconst storage = createMockStorage();\n\n// Use in auth init\ninitAuth({ baseUrl: 'https://api.test', storage });\n\n// Assert calls\nexpect(storage.getCalls('setItem')).toHaveLength(2);"
            },
            {
              "name": "createMockStorage",
              "signature": "(options?: MockStorageOptions) => MockStorageAdapter",
              "description": "Create a mock storage adapter"
            },
            {
              "name": "createMockStorageWithAuth",
              "signature": "(tokens?: { accessToken?: string; refreshToken?: string }) => MockStorageAdapter",
              "description": "Create storage pre-populated with auth tokens"
            },
            {
              "name": "MockFetchInstance",
              "signature": "class",
              "description": "Mock fetch implementation with route handling",
              "example": "import { createMockFetch, mockLoginSuccess } from '@classic-homes/auth/testing';\n\nconst mockFetch = createMockFetch();\nmockFetch.addRoute('POST', '/auth/login', { data: mockLoginSuccess });\n\n// Use in auth init\ninitAuth({ baseUrl: 'https://api.test', fetch: mockFetch.fetch });\n\n// Assert calls\nmockFetch.assertCalled('/auth/login');\nmockFetch.assertCalledWith('/auth/login', { body: { username: 'test' } });"
            },
            {
              "name": "createMockFetch",
              "signature": "(options?: MockFetchOptions) => MockFetchInstance",
              "description": "Create a mock fetch instance"
            },
            {
              "name": "createMockFetchWithRoutes",
              "signature": "(routes: MockRoute[]) => MockFetchInstance",
              "description": "Create mock fetch with pre-configured routes"
            },
            {
              "name": "MockAuthStore",
              "signature": "class",
              "description": "Mock auth store for Svelte testing"
            },
            {
              "name": "createMockAuthStore",
              "signature": "(options?: MockAuthStoreOptions) => MockAuthStore",
              "description": "Create a mock auth store"
            },
            {
              "name": "createAuthenticatedMockStore",
              "signature": "(user?: User) => MockAuthStore",
              "description": "Create a mock store in authenticated state"
            },
            {
              "name": "createMockAuthActions",
              "signature": "() => MockAuthActions",
              "description": "Create mock authActions object for testing"
            },
            {
              "name": "createMockIsAuthenticated",
              "signature": "(value?: boolean) => { subscribe: (fn) => () => void }",
              "description": "Create mock isAuthenticated derived store"
            },
            {
              "name": "createMockCurrentUser",
              "signature": "(user?: User) => { subscribe: (fn) => () => void }",
              "description": "Create mock currentUser derived store"
            }
          ]
        },
        "helpers": {
          "description": "Test setup and assertion helpers",
          "functions": [
            {
              "name": "setupTestAuth",
              "signature": "(options?: SetupTestAuthOptions) => TestAuthContext",
              "description": "Complete test setup with all mocks configured",
              "example": "import { describe, it, beforeEach, afterEach } from 'vitest';\nimport { setupTestAuth, mockUser, assertAuthenticated } from '@classic-homes/auth/testing';\nimport { authService } from '@classic-homes/auth/core';\n\ndescribe('Auth', () => {\n  let ctx: TestAuthContext;\n\n  beforeEach(() => {\n    ctx = setupTestAuth();\n  });\n\n  afterEach(() => ctx.cleanup());\n\n  it('handles login', async () => {\n    const response = await authService.login({\n      username: 'test@example.com',\n      password: 'password'\n    });\n\n    assertAuthenticated(ctx.mockStore);\n    ctx.mockFetch.assertCalled('/auth/login');\n  });\n});"
            },
            {
              "name": "quickSetupAuth",
              "signature": "() => { cleanup: () => void }",
              "description": "Minimal setup for quick tests"
            },
            {
              "name": "withTestAuth",
              "signature": "(testFn: (ctx: TestAuthContext) => Promise<void>) => () => Promise<void>",
              "description": "Wrapper that handles setup and cleanup"
            },
            {
              "name": "withTestAuthEach",
              "signature": "(callback: (ctx: TestAuthContext) => void) => void",
              "description": "Setup for each test in a suite"
            },
            {
              "name": "createTestAuthHelpers",
              "signature": "(options?: SetupTestAuthOptions) => TestAuthHelpers",
              "description": "Create test helpers with shared context"
            },
            {
              "name": "initAuthWithMocks",
              "signature": "(config: Partial<AuthConfig>) => void",
              "description": "Initialize auth with mock configuration"
            },
            {
              "name": "resetTestAuth",
              "signature": "() => void",
              "description": "Reset all test auth state"
            },
            {
              "name": "authScenarios",
              "signature": "{ authenticated, unauthenticated, admin, ssoUser, mfaEnabled, unverifiedEmail, inactive, expiredToken }",
              "description": "Pre-configured test scenarios"
            },
            {
              "name": "applyScenario",
              "signature": "(store: MockAuthStore, scenario: AuthScenario) => void",
              "description": "Apply a test scenario to the store"
            },
            {
              "name": "applyScenarios",
              "signature": "(store: MockAuthStore, scenarios: AuthScenario[]) => void",
              "description": "Apply multiple test scenarios to the store"
            },
            {
              "name": "getScenarioState",
              "signature": "(scenario: AuthScenario) => ScenarioState",
              "description": "Get the state for a scenario"
            },
            {
              "name": "configureMFAFlow",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for MFA flow testing"
            },
            {
              "name": "configureTokenRefresh",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for token refresh testing"
            },
            {
              "name": "configureSSOLogout",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for SSO logout flow"
            },
            {
              "name": "configureAuthFailure",
              "signature": "(mockFetch: MockFetchInstance, error?: string) => void",
              "description": "Configure mock fetch to fail authentication"
            },
            {
              "name": "configureRateLimiting",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch to return rate limit errors"
            },
            {
              "name": "configureMFAEnrollmentFlow",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for MFA enrollment flow"
            },
            {
              "name": "configurePasswordResetFlow",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for password reset flow"
            },
            {
              "name": "configureSessionManagementFlow",
              "signature": "(mockFetch: MockFetchInstance) => void",
              "description": "Configure mock fetch for session management"
            },
            {
              "name": "simulateLogin",
              "signature": "(store: MockAuthStore, user?: User, tokens?: TokenPair) => void",
              "description": "Simulate a login action on the store"
            },
            {
              "name": "simulateLogout",
              "signature": "(store: MockAuthStore) => void",
              "description": "Simulate a logout action on the store"
            },
            {
              "name": "simulateTokenRefresh",
              "signature": "(store: MockAuthStore, newToken?: string) => void",
              "description": "Simulate a token refresh on the store"
            },
            {
              "name": "simulateProfileUpdate",
              "signature": "(store: MockAuthStore, userData: Partial<User>) => void",
              "description": "Simulate a profile update on the store"
            },
            {
              "name": "createTestUserWithPermissions",
              "signature": "(permissions: string[]) => User",
              "description": "Create a test user with specific permissions"
            },
            {
              "name": "createTestAdminUser",
              "signature": "() => User",
              "description": "Create a test admin user"
            },
            {
              "name": "createTestSSOUser",
              "signature": "(provider?: string) => User",
              "description": "Create a test SSO user"
            },
            {
              "name": "assertAuthenticated",
              "signature": "(state: AuthState) => void",
              "description": "Assert that state is authenticated"
            },
            {
              "name": "assertUnauthenticated",
              "signature": "(state: AuthState) => void",
              "description": "Assert that state is unauthenticated"
            },
            {
              "name": "assertHasUser",
              "signature": "(state: AuthState, userId?: string) => void",
              "description": "Assert state has a user (optionally with specific ID)"
            },
            {
              "name": "assertHasPermissions",
              "signature": "(user: User, permissions: string[]) => void",
              "description": "Assert user has specific permissions"
            },
            {
              "name": "assertLacksPermissions",
              "signature": "(user: User, permissions: string[]) => void",
              "description": "Assert user lacks specific permissions"
            },
            {
              "name": "assertHasRoles",
              "signature": "(user: User, roles: string[]) => void",
              "description": "Assert user has specific roles"
            },
            {
              "name": "assertLacksRoles",
              "signature": "(user: User, roles: string[]) => void",
              "description": "Assert user lacks specific roles"
            },
            {
              "name": "assertTokenValid",
              "signature": "(token: string) => void",
              "description": "Assert token is valid and not expired"
            },
            {
              "name": "assertTokenExpired",
              "signature": "(token: string) => void",
              "description": "Assert token is expired"
            },
            {
              "name": "assertTokenHasClaims",
              "signature": "(token: string, claims: string[]) => void",
              "description": "Assert token has specific claims"
            },
            {
              "name": "assertTokenSubject",
              "signature": "(token: string, expectedSub: string) => void",
              "description": "Assert token has specific subject"
            },
            {
              "name": "assertApiCalled",
              "signature": "(mockFetch: MockFetchInstance, method: string, path: string, options?: AssertApiCalledOptions) => void",
              "description": "Assert API endpoint was called"
            },
            {
              "name": "assertApiNotCalled",
              "signature": "(mockFetch: MockFetchInstance, method: string, path: string) => void",
              "description": "Assert API endpoint was not called"
            },
            {
              "name": "assertApiCallOrder",
              "signature": "(mockFetch: MockFetchInstance, calls: Array<{ method: string; path: string }>) => void",
              "description": "Assert API calls were made in specific order"
            },
            {
              "name": "assertStoreMethodCalled",
              "signature": "(store: MockAuthStore, method: string, options?: AssertStoreMethodCalledOptions) => void",
              "description": "Assert store method was called"
            },
            {
              "name": "assertStoreMethodNotCalled",
              "signature": "(store: MockAuthStore, method: string) => void",
              "description": "Assert store method was not called"
            },
            {
              "name": "assertRequiresMFA",
              "signature": "(response: LoginResponse) => void",
              "description": "Assert login response requires MFA"
            },
            {
              "name": "assertNoMFARequired",
              "signature": "(response: LoginResponse) => void",
              "description": "Assert login response does not require MFA"
            },
            {
              "name": "assertEmailVerified",
              "signature": "(user: User) => void",
              "description": "Assert user email is verified"
            },
            {
              "name": "assertEmailNotVerified",
              "signature": "(user: User) => void",
              "description": "Assert user email is not verified"
            },
            {
              "name": "assertUserActive",
              "signature": "(user: User) => void",
              "description": "Assert user is active"
            },
            {
              "name": "assertUserInactive",
              "signature": "(user: User) => void",
              "description": "Assert user is inactive"
            },
            {
              "name": "assertAuthMethod",
              "signature": "(user: User, method: 'password' | 'oauth' | 'both') => void",
              "description": "Assert user has specific auth method"
            }
          ],
          "types": [
            {
              "name": "TestAuthContext",
              "description": "Context returned from setupTestAuth",
              "properties": [
                {
                  "name": "mockFetch",
                  "type": "MockFetchInstance",
                  "required": true,
                  "description": "Mock fetch instance"
                },
                {
                  "name": "mockStorage",
                  "type": "MockStorageAdapter",
                  "required": true,
                  "description": "Mock storage adapter"
                },
                {
                  "name": "mockStore",
                  "type": "MockAuthStore",
                  "required": true,
                  "description": "Mock auth store"
                },
                {
                  "name": "cleanup",
                  "type": "() => void",
                  "required": true,
                  "description": "Cleanup function"
                }
              ]
            },
            {
              "name": "SetupTestAuthOptions",
              "description": "Options for setupTestAuth",
              "properties": [
                {
                  "name": "authenticated",
                  "type": "boolean",
                  "required": false,
                  "description": "Start in authenticated state"
                },
                {
                  "name": "user",
                  "type": "User",
                  "required": false,
                  "description": "User for authenticated state"
                },
                {
                  "name": "tokens",
                  "type": "object",
                  "required": false,
                  "description": "Tokens for authenticated state"
                }
              ]
            }
          ]
        }
      }
    }
  }
}